import React from 'react'
import { Suggestion } from '../../types/mockup'

interface ApplyConfirmationModalProps {
  suggestion: Suggestion
  isOpen: boolean
  onConfirm: () => void
  onCancel: () => void
}

export const ApplyConfirmationModal: React.FC<ApplyConfirmationModalProps> = ({
  suggestion,
  isOpen,
  onConfirm,
  onCancel
}) => {
  if (!isOpen) return null

  const formatTime = (date: Date): string => {
    return new Date(date).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    })
  }

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount)
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ 
            margin: '0 0 8px 0', 
            fontSize: '20px', 
            fontWeight: 600,
            color: '#1a1a1a'
          }}>
            Apply Suggestion: {suggestion.title}
          </h2>
          <p style={{ 
            margin: 0, 
            fontSize: '14px', 
            color: '#666' 
          }}>
            {suggestion.headline}
          </p>
        </div>

        {/* Timeline Changes */}
        {suggestion.operationalImpact.timelineChanges.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: 600, 
              margin: '0 0 12px 0',
              color: '#1a1a1a',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              🔄 Timeline Changes
            </h3>
            <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px' }}>
              {suggestion.operationalImpact.timelineChanges.map((change, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  padding: '4px 0',
                  borderBottom: index < suggestion.operationalImpact.timelineChanges.length - 1 ? '1px solid #e0e0e0' : 'none'
                }}>
                  <span style={{ fontSize: '14px', color: '#333' }}>
                    {change.taskId.replace('task-', '').replace('-', ' ')}:
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>
                    {formatTime(change.oldTime)} → {formatTime(change.newTime)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notifications */}
        {suggestion.proposedChanges.notifications && suggestion.proposedChanges.notifications.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: 600, 
              margin: '0 0 12px 0',
              color: '#1a1a1a',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              📱 Notifications to Send
            </h3>
            <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px' }}>
              {suggestion.proposedChanges.notifications.map((notification, index) => (
                <div key={index} style={{ 
                  marginBottom: index < suggestion.proposedChanges.notifications.length - 1 ? '12px' : '0',
                  padding: '8px',
                  background: 'white',
                  borderRadius: '6px',
                  border: '1px solid #e0e0e0'
                }}>
                  <div style={{ 
                    fontSize: '13px', 
                    fontWeight: 600, 
                    color: '#666',
                    marginBottom: '4px'
                  }}>
                    {notification.channel.toUpperCase()} to {notification.recipientId.replace('-', ' ')}
                  </div>
                  <div style={{ fontSize: '14px', color: '#333' }}>
                    "{notification.message}"
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Impact Summary */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: 600, 
            margin: '0 0 12px 0',
            color: '#1a1a1a',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            📊 Impact Summary
          </h3>
          <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Guests Affected</div>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>{suggestion.impact.guestsAffected || 0}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Vendors Involved</div>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>{suggestion.operationalImpact.affectedVendors.length}</div>
              </div>
              {suggestion.operationalImpact.financialImpact > 0 && (
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Cost Impact</div>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: '#f57c00' }}>
                    +{formatCurrency(suggestion.operationalImpact.financialImpact)}
                  </div>
                </div>
              )}
              <div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Risk Level</div>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 600,
                  color: suggestion.riskLevel === 'critical' ? '#f44336' : suggestion.riskLevel === 'warning' ? '#ff9800' : '#2196f3'
                }}>
                  {suggestion.riskLevel.charAt(0).toUpperCase() + suggestion.riskLevel.slice(1)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cascading Changes */}
        {suggestion.impact.cascadingChanges && suggestion.impact.cascadingChanges.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: 600, 
              margin: '0 0 12px 0',
              color: '#1a1a1a',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              🔗 Additional Effects
            </h3>
            <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px' }}>
              <ul style={{ margin: 0, paddingLeft: '16px' }}>
                {suggestion.impact.cascadingChanges.map((change, index) => (
                  <li key={index} style={{ fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                    {change}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'flex-end',
          borderTop: '1px solid #e0e0e0',
          paddingTop: '20px'
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '12px 24px',
              borderRadius: '6px',
              border: '1px solid #ddd',
              background: 'white',
              color: '#666',
              fontWeight: 600,
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#f5f5f5'
              e.currentTarget.style.borderColor = '#999'
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'white'
              e.currentTarget.style.borderColor = '#ddd'
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '12px 24px',
              borderRadius: '6px',
              border: 'none',
              background: '#4caf50',
              color: 'white',
              fontWeight: 600,
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#45a049'}
            onMouseOut={(e) => e.currentTarget.style.background = '#4caf50'}
          >
            Confirm & Apply
          </button>
        </div>
      </div>
    </div>
  )
}

export default ApplyConfirmationModal